import { call, fork, put, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import moment from 'moment';
import type { Action } from 'redux';

import {
    DOMAIN_ASR_ERROR_DATA,
    DOMAIN_CALL_DATA,
    DOMAIN_CALL_ENDED,
    DOMAIN_CALL_STARTED,
    DOMAIN_INITIAL_CALL_DATA
} from '../../actions/actionTypes/domain';

import {
    CONFIGURATION_LOADED,
    SET_ASR_CONFIG_ERRORS,
    SET_BLACKLIST,
    SET_CALL_RESULT,
    SET_ELAPSED_SECONDS,
    SET_IS_STARTED,
    SET_LOUDNESS,
    SET_NEW_SPEECH_RATE,
    SET_PHRASE_INTEGRATION_WORDS,
    SET_SPEECH_PACE,
    SET_SPEECH_RATE,
    SET_SPEECH_RATIO,
    SET_TIMELINE_TRANSCRIPT,
    SET_WHITELIST
} from '../../actions/actionTypes/trainer';
import { hideNewCallMessage } from '../../actions/trainer';
import { CALL_STATE_TRANSITION_DURATION } from '../../../constants/appRuntime';
import { SET_CALL_ID } from '../../actions/actionTypes/debug';
import type { KeyPhraseIntegrationWords, TrainerAsrConfigErrors, TrainerPhraseGroup } from '../../types';
import type { TranscriptItem } from '../../../utils/transcript';

type SagaAction = {
    payload?: {
        blacklist?: TrainerPhraseGroup[];
        whitelist?: TrainerPhraseGroup[];
        newSpeechRate?: number;
        speechRatio?: number;
        speechPace?: number;
        speechRate?: number;
        loudness?: number;
        callDuration?: number;
        timelineTranscripts?: TranscriptItem[];
        keyPhraseIntegrationWords?: KeyPhraseIntegrationWords | null;
        systemSoundRecognition?: boolean;
        callId?: string | number | null;
        asrConfigErrors?: TrainerAsrConfigErrors;
    };
} & Action<string>;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toPhraseGroups(value: unknown): TrainerPhraseGroup[] {
    return Array.isArray(value) ? value.filter((item): item is TrainerPhraseGroup => isRecord(item)) : [];
}

function toTranscriptItems(value: unknown): TranscriptItem[] {
    return Array.isArray(value) ? value.filter((item): item is TranscriptItem => isRecord(item)) : [];
}

function toKeyPhraseWords(value: unknown): KeyPhraseIntegrationWords | null {
    if (!isRecord(value)) {
        return null;
    }
    const start = Array.isArray(value.start) ? value.start.filter((item): item is string => typeof item === 'string') : undefined;
    const stop = Array.isArray(value.stop) ? value.stop.filter((item): item is string => typeof item === 'string') : undefined;
    if (!start && !stop) {
        return null;
    }
    return { start, stop };
}

const normalizeAsrConfigErrors = (value: unknown): TrainerAsrConfigErrors => {
    if (!value || typeof value !== 'object') {
        return {};
    }
    const candidate = value as { noWhitelist?: unknown; noBlacklist?: unknown; noTeam?: unknown };
    return {
        noWhitelist: typeof candidate.noWhitelist === 'boolean' ? candidate.noWhitelist : undefined,
        noBlacklist: typeof candidate.noBlacklist === 'boolean' ? candidate.noBlacklist : undefined,
        noTeam: typeof candidate.noTeam === 'boolean' ? candidate.noTeam : undefined
    };
};

const normalizeSystemSoundRecognition = (value: unknown): boolean => {
    return Boolean(value);
};

const normalizeCallId = (value: unknown): string | number | null => {
    if (typeof value === 'string' || typeof value === 'number') {
        return value;
    }
    return null;
};

export function* doProcessASRData(action: SagaAction): Generator {
    const {
        blacklist = null,
        whitelist = null,
        newSpeechRate = null,
        speechRatio = null,
        speechPace = null,
        speechRate = null,
        loudness = null,
        callDuration = null,
        timelineTranscripts = null,
        keyPhraseIntegrationWords = null
    } = action.payload || {};

    const safeBlacklist = toPhraseGroups(blacklist);
    if (safeBlacklist.length > 0) {
        yield put({ type: SET_BLACKLIST, payload: { blacklist: safeBlacklist } });
    }

    const safeWhitelist = toPhraseGroups(whitelist);
    if (safeWhitelist.length > 0) {
        yield put({ type: SET_WHITELIST, payload: { whitelist: safeWhitelist } });
    }

    if (typeof loudness === 'number' && typeof speechRatio === 'number') {
        yield put({ type: SET_LOUDNESS, payload: { loudness } });
    }

    const safeTimelineTranscripts = toTranscriptItems(timelineTranscripts);
    if (safeTimelineTranscripts.length > 0) {
        yield put({ type: SET_TIMELINE_TRANSCRIPT, payload: { timelineTranscripts: safeTimelineTranscripts } });
    }

    if (typeof callDuration === 'number' && callDuration >= 0) {
        yield put({ type: SET_ELAPSED_SECONDS, payload: { elapsedSeconds: callDuration } });
    }

    if (typeof newSpeechRate === 'number') {
        yield put({ type: SET_NEW_SPEECH_RATE, payload: { newSpeechRate } });
    }

    if (typeof speechPace === 'number') {
        yield put({ type: SET_SPEECH_PACE, payload: { speechPace } });
    }

    if (typeof speechRate === 'number') {
        yield put({ type: SET_SPEECH_RATE, payload: { speechRate } });
    }

    if (typeof speechRatio === 'number') {
        yield put({ type: SET_SPEECH_RATIO, payload: { speechRatio } });
    }

    const safeKeyPhraseIntegrationWords = toKeyPhraseWords(keyPhraseIntegrationWords);
    if (safeKeyPhraseIntegrationWords) {
        yield put({
            type: SET_PHRASE_INTEGRATION_WORDS,
            payload: {
                keyPhraseIntegrationWords: safeKeyPhraseIntegrationWords
            }
        });
    }
}

export function* doProcessCallStart(action: SagaAction): Generator {
    const systemSoundRecognition = normalizeSystemSoundRecognition(action.payload?.systemSoundRecognition);
    const callId = normalizeCallId(action.payload?.callId);

    yield put({
        type: SET_IS_STARTED,
        payload: {
            isStarted: true,
            systemSoundRecognition
        }
    });

    yield put({
        type: SET_CALL_ID,
        payload: { callId, timestamp: moment().format() }
    });

    yield call(delay, CALL_STATE_TRANSITION_DURATION);
    yield put(hideNewCallMessage());
}

export function* doProcessCallEnd(action: SagaAction): Generator {
    yield put({
        type: SET_CALL_RESULT,
        payload: { isLastCallResult: true, whitelist: action.payload?.whitelist }
    });
    yield put({ type: SET_IS_STARTED, payload: { isStarted: false } });
    yield call(doProcessASRData, action);
    yield put({ type: SET_ELAPSED_SECONDS, payload: { elapsedSeconds: 0 } });
}

export function* handleCallStart(): Generator {
    yield takeLatest(DOMAIN_CALL_STARTED, doProcessCallStart);
}

export function* handleCallData(): Generator {
    yield takeLatest(DOMAIN_CALL_DATA, doProcessASRData);
}

export function* handleInitialCallData(): Generator {
    function* handler(action: SagaAction): Generator {
        yield put({ type: CONFIGURATION_LOADED });
        yield call(doProcessASRData, action);
    }
    yield takeLatest(DOMAIN_INITIAL_CALL_DATA, handler);
}

export function* handleAsrErrorData(): Generator {
    function* handler(action: SagaAction): Generator {
        const { asrConfigErrors } = action.payload || {};
        const normalized = normalizeAsrConfigErrors(asrConfigErrors);
        if (normalized.noWhitelist !== undefined || normalized.noBlacklist !== undefined || normalized.noTeam !== undefined) {
            yield put({ type: SET_ASR_CONFIG_ERRORS, payload: { asrConfigErrors: normalized } });
        }
    }

    yield takeLatest(DOMAIN_ASR_ERROR_DATA, handler);
}

export function* handleCallEnd(): Generator {
    yield takeLatest(DOMAIN_CALL_ENDED, doProcessCallEnd);
}

export function* processASRData(): Generator {
    yield fork(handleCallStart);
    yield fork(handleCallData);
    yield fork(handleCallEnd);
}

export default [handleInitialCallData(), processASRData(), handleAsrErrorData()];
